import { v4 as uuid } from 'uuid';
import { StateViewModel } from '../models/viewModels/StateViewModel';
import { ContentActionViewModel } from '../models/viewModels/ContentActionViewModel';
import { InputViewModel } from '../models/viewModels/InputViewModel';
import { Input } from '../models/Input';
import { OutputViewModel } from '../models/viewModels/OutputViewModel';
import { ActionViewModel } from '../models/viewModels/ActionViewModel';
import { ActionType } from '../models/Action';
import { Condition, ValueSource, ConditionComparison } from '../models/Condition';
import { ExecuteScriptActionSettings } from '../models/ExecuteScriptActionSettings';
import { ProcessCommandActionSettings } from '../models/ProcessCommandActionSettings';
import { ForwardMessageToDeskActionSettings } from '../models/ForwardMessageToDeskActionSettings';
import { ForwardToDeskActionSettings } from '../models/ForwardToDeskActionSettings';
import { LeavingFromDeskActionSettings } from '../models/LeavingFromDeskActionSettings';
import { TicketStatus } from '../models/TicketStatus';
import { CreateTicketStatus } from '../models/CreateTicketStatus';
import { BuilderFeatures } from '../BuilderFeatures';
import { CommandMethods } from 'types/CommandMethods';
import { getNewSmartSalesState } from '../smartSales/state/States';
import { getNewBlipChatCopyAndPasteState } from '../exclusiveComponents/state/States';
import { getAiAgentsState } from '../aiAgent/state/State';
import { getPaymentState } from '../payment/state/State';
export const SURVEY_ID_PREFIX = 'survey:';
export const DESK_ID_PREFIX = 'desk:';
export const SUBFLOW_ID_PREFIX = 'subflow:';
export const ANSWERS_ID_PREFIX = 'answers:';
export const PAYMENT_ID_PREFIX = 'payment:';
export const FORWARD_TO_DESK_STATUS_VARIABLE = 'desk_forwardToDeskState_status';
export const DESK_TICKET_STATUS_VARIABLE = 'input.content@status';
export const DESK_TICKET_TYPE = 'application/vnd.iris.ticket+json';
const DESK_STATE_BUILDER_VERSION_NUMBER = '0';
const DESK_CURRENT_STATE_VERSION = 2.0;
export const DESK_IGNORE_CREATE_TICKET_USER_MESSAGE_MIN_STATE_VERSION = 1.0;
export const DESK_AUTOCLOSING_MIN_STATE_VERSION = 2.0;
export const DESK_REDIRECT_OUT_OF_ATTENDANCE_TIME_MIN_STATE_VERSION = 3.0;
export const DESK_TEMPLATE_TYPE = 'desk';
export const SUBFLOW_TEMPLATE_TYPE = 'subflow';
export const CONTENT_TEMPLATE_TYPE = 'content';
export const AI_ANSWERS_TEMPLATE_TYPE = 'answers';
export const PAYMENT_TEMPLATE_TYPE = 'payment';
export const STATE_ID_ONBOARDING = 'onboarding';
export const STATE_ID_FALLBACK = 'fallback';
export const STATE_ID_WELCOME = 'welcome';
export const STATE_ID_END = 'end';
export const STATE_ID_ERROR = 'error';
export const STATE_ID_FIRST_BLOCK_SUBFLOW = 'first_block';
const USER_INPUT_TRANSLATE_KEY = 'builder-tabs-content.media.userInput';
export class BuilderStateService {
    static $inject = ['$translate', 'MESSAGINGHUBDOMAIN'];
    constructor($translate, MESSAGINGHUBDOMAIN) {
        'ngInject';
        this.$translate = $translate;
        this.MESSAGINGHUBDOMAIN = MESSAGINGHUBDOMAIN;
        this.deskStateVersion = DESK_CURRENT_STATE_VERSION;
        this.init();
    }
    async init() {
        await this.checkFeatures();
        this.resolveVersion();
    }
    async checkFeatures() {
        const [mergeTunnelContactDeskStateActionEnabled, forwardToDeskAttendanceBoxEnabled,] = await Promise.all([
            BuilderFeatures.builderMergeTunnelContactDeskStateEnabled(),
            BuilderFeatures.isForwardToDeskAttendanceBoxEnabled(),
        ]);
        this.mergeTunnelContactDeskStateActionEnabled = mergeTunnelContactDeskStateActionEnabled;
        this.isForwardToDeskAttendanceBoxEnabled = forwardToDeskAttendanceBoxEnabled;
    }
    createState(template, title, stateId, extras, imageUri, shortNameOfSubflow, leavingFromDeskSettings) {
        switch (template) {
            case DESK_TEMPLATE_TYPE:
                return this.createDeskState(title, stateId, leavingFromDeskSettings);
            case SUBFLOW_TEMPLATE_TYPE:
                return this.createSubflowState(shortNameOfSubflow, title, stateId, extras, imageUri);
            case AI_ANSWERS_TEMPLATE_TYPE:
                return this.createAiAnswersState(title, stateId, extras?.assistantId);
            case PAYMENT_TEMPLATE_TYPE:
                return getPaymentState(title, this.$translate.instant(USER_INPUT_TRANSLATE_KEY));
            case CONTENT_TEMPLATE_TYPE:
            default:
                return this.createContentState(title);
        }
    }
    resolveVersion() {
        if (this.isForwardToDeskAttendanceBoxEnabled && this.deskStateVersion < DESK_REDIRECT_OUT_OF_ATTENDANCE_TIME_MIN_STATE_VERSION) {
            this.deskStateVersion = DESK_REDIRECT_OUT_OF_ATTENDANCE_TIME_MIN_STATE_VERSION;
            return;
        }
    }
    createSubflowState(shortNameOfSubflow, title, stateId, extras, imageUri) {
        const id = stateId || `${SUBFLOW_ID_PREFIX}${uuid()}`;
        return new StateViewModel({
            id: id,
            root: false,
            shortNameOfSubflow,
            $imageUri: imageUri,
            $contentActions: [
                new ContentActionViewModel({
                    input: InputViewModel.create(this.$translate.instant(USER_INPUT_TRANSLATE_KEY), new Input({ bypass: false })),
                }),
            ],
            $title: title,
            $conditionOutputs: [],
            $defaultOutput: new OutputViewModel({ stateId: 'fallback' }),
        });
    }
    createContentState(title) {
        return new StateViewModel({
            id: uuid(),
            root: false,
            $contentActions: [
                new ContentActionViewModel({
                    input: InputViewModel.create(this.$translate.instant(USER_INPUT_TRANSLATE_KEY), new Input({ bypass: false })),
                }),
            ],
            $title: title,
            $conditionOutputs: [],
            $defaultOutput: new OutputViewModel({ stateId: 'fallback' }),
        });
    }
    createAiAnswersState(title, stateId, assistantId) {
        const id = stateId || `${ANSWERS_ID_PREFIX}${uuid()}`;
        return new StateViewModel({
            id: id,
            root: false,
            $contentActions: [
                new ContentActionViewModel({
                    input: InputViewModel.create(this.$translate.instant(USER_INPUT_TRANSLATE_KEY), new Input({ bypass: false })),
                }),
            ],
            $title: title,
            $leavingCustomActions: [
                new ActionViewModel({
                    type: ActionType.ProcessAnswers,
                    $title: 'Process answers action',
                    $invalid: false,
                    settings: {
                        'UserInput': '{{input.content}}',
                        'ContactId': '{{contact.identity}}',
                        'AssistantId': assistantId,
                    },
                    conditions: [],
                }),
            ],
            $conditionOutputs: [],
            $defaultOutput: new OutputViewModel({ stateId: 'fallback' }),
        });
    }
    createDeskStateMergeTunnelContactActions(id) {
        return [
            new ActionViewModel({
                type: ActionType.ExecuteScript,
                $invalid: false,
                conditions: [
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'state.previous.id',
                        comparison: ConditionComparison.NotEquals,
                        values: [id],
                    }),
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'tunnel.identity',
                        comparison: ConditionComparison.Exists,
                        values: [],
                    }),
                ],
                settings: new ExecuteScriptActionSettings({
                    function: 'run',
                    source: 'function run(contact, tunnel_identity) {\n    contact = JSON.parse(contact);\n    contact.identity = tunnel_identity;\n    return contact;\n}',
                    inputVariables: [
                        'contact.serialized',
                        'tunnel.identity',
                    ],
                    outputVariable: 'deskUpdateTunnelContactBody',
                }),
            }),
            new ActionViewModel({
                type: ActionType.ProcessCommand,
                $invalid: false,
                conditions: [
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'state.previous.id',
                        comparison: ConditionComparison.NotEquals,
                        values: [id],
                    }),
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'tunnel.identity',
                        comparison: ConditionComparison.Exists,
                        values: [],
                    }),
                ],
                settings: new ProcessCommandActionSettings({
                    to: `postmaster@${this.MESSAGINGHUBDOMAIN}`,
                    from: '{{application.identity}}',
                    method: CommandMethods.merge,
                    uri: '/contacts',
                    type: 'application/vnd.lime.contact+json',
                    resource: '{{deskUpdateTunnelContactBody}}',
                    variable: 'mergeRouterContactWithTunnelCommandResponse',
                }),
            }),
        ];
    }
    createDeskState(title, stateId, leavingFromDeskSettings) {
        if (this.isForwardToDeskAttendanceBoxEnabled) {
            return this.createDeskStateWithForwardAndLeaving(title, stateId, leavingFromDeskSettings);
        }
        return this.createDeskStateForwardMessage(title, stateId);
    }
    createSmartSalesState(title, token) {
        const waCtaMessage = {
            header: this.$translate.instant('builder.smartSales.defaultCtaMessage.header'),
            body: this.$translate.instant('builder.smartSales.defaultCtaMessage.body'),
            footer: this.$translate.instant('builder.smartSales.defaultCtaMessage.footer'),
            button: this.$translate.instant('builder.smartSales.defaultCtaMessage.button'),
        };
        const defaultDisabledCtaMsg = this.$translate.instant('builder.smartSales.cta.configure.defaultCatalogMessage');
        return getNewSmartSalesState(title, { token, waCtaMessage, defaultDisabledCtaMsg }, this.$translate.instant(USER_INPUT_TRANSLATE_KEY));
    }
    createBlipChatCopyAndPaste(title) {
        const copyAndPasteContent = {
            header: this.$translate.instant('builder.exclusiveComponents.components.channels.blipChat.copyAndPaste.content.header'),
            body: this.$translate.instant('builder.exclusiveComponents.components.channels.blipChat.copyAndPaste.content.body'),
            footer: this.$translate.instant('builder.exclusiveComponents.components.channels.blipChat.copyAndPaste.content.footer'),
            button: {
                text: this.$translate.instant('builder.exclusiveComponents.components.channels.blipChat.copyAndPaste.content.button.text'),
                value: this.$translate.instant('builder.exclusiveComponents.components.channels.blipChat.copyAndPaste.content.button.value'),
            },
        };
        return getNewBlipChatCopyAndPasteState(title, copyAndPasteContent);
    }
    createAiAgentState(title, configuration) {
        return getAiAgentsState(title, this.$translate.instant(USER_INPUT_TRANSLATE_KEY), configuration);
    }
    createPaymentState(title, _configuration) {
        return getPaymentState(title, this.$translate.instant(USER_INPUT_TRANSLATE_KEY));
    }
    createDeskStateWithForwardAndLeaving(title, stateId, leavingFromDeskSettings) {
        const id = stateId || `${DESK_ID_PREFIX}${uuid()}`;
        const enteringCustomActions = [
            new ActionViewModel({
                type: ActionType.ForwardToDesk,
                conditions: [],
                settings: new ForwardToDeskActionSettings(),
            }),
        ];
        const afterStateChangedActions = [
            new ActionViewModel({
                type: ActionType.LeavingFromDesk,
                conditions: [],
                settings: new LeavingFromDeskActionSettings(leavingFromDeskSettings),
            }),
        ];
        const conditionOutputs = [
            new OutputViewModel({
                $isDeskOutput: true,
                conditions: [
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'input.type',
                        comparison: ConditionComparison.Equals,
                        values: [DESK_TICKET_TYPE],
                    }),
                    new Condition({
                        source: ValueSource.Context,
                        variable: DESK_TICKET_STATUS_VARIABLE,
                        comparison: ConditionComparison.Equals,
                        values: [TicketStatus.ClosedAttendant],
                    }),
                ],
            }),
            new OutputViewModel({
                $isDeskOutput: true,
                conditions: [
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'input.type',
                        comparison: ConditionComparison.Equals,
                        values: [DESK_TICKET_TYPE],
                    }),
                    new Condition({
                        source: ValueSource.Context,
                        variable: DESK_TICKET_STATUS_VARIABLE,
                        comparison: ConditionComparison.Equals,
                        values: [TicketStatus.ClosedClient],
                    }),
                ],
            }),
            new OutputViewModel({
                $isDeskOutput: true,
                conditions: [
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'input.type',
                        comparison: ConditionComparison.Equals,
                        values: [DESK_TICKET_TYPE],
                    }),
                    new Condition({
                        source: ValueSource.Context,
                        variable: DESK_TICKET_STATUS_VARIABLE,
                        comparison: ConditionComparison.Equals,
                        values: [TicketStatus.ClosedClientInactivity],
                    }),
                ],
            }),
            new OutputViewModel({
                $isDeskOutput: true,
                $isDeskDefaultOutput: true,
                conditions: [
                    new Condition({
                        source: ValueSource.Context,
                        variable: FORWARD_TO_DESK_STATUS_VARIABLE,
                        comparison: ConditionComparison.Equals,
                        values: [CreateTicketStatus.Error],
                    }),
                ],
                stateId: 'fallback',
                $invalid: false,
            }),
        ];
        const deskVersion = `${this.deskStateVersion.toFixed(1)}.${DESK_STATE_BUILDER_VERSION_NUMBER}`;
        return new StateViewModel({
            id: id,
            deskStateVersion: deskVersion,
            root: false,
            $contentActions: [
                new ContentActionViewModel({
                    input: InputViewModel.create(this.$translate.instant(USER_INPUT_TRANSLATE_KEY), new Input({
                        bypass: false,
                        conditions: [
                            new Condition({
                                source: ValueSource.Context,
                                variable: FORWARD_TO_DESK_STATUS_VARIABLE,
                                comparison: ConditionComparison.Equals,
                                values: [CreateTicketStatus.Success],
                            }),
                        ],
                    }), false),
                }),
            ],
            $title: title,
            $enteringCustomActions: enteringCustomActions,
            $afterStateChangedActions: afterStateChangedActions,
            $conditionOutputs: conditionOutputs,
            $defaultOutput: new OutputViewModel({ stateId: id }),
        });
    }
    createDeskStateForwardMessage(title, stateId) {
        const id = stateId || `${DESK_ID_PREFIX}${uuid()}`;
        const helpDeskHasNoTicketCondition = new Condition({
            source: ValueSource.Context,
            variable: 'helpDeskHasTicket',
            comparison: ConditionComparison.NotEquals,
            values: ['true'],
        });
        const checkAndOpenTicketEnteringCustomActions = [
            new ActionViewModel({
                type: ActionType.ExecuteScript,
                $title: 'GetEncodedContactIdentity',
                $invalid: false,
                conditions: [
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'state.previous.id',
                        comparison: ConditionComparison.NotEquals,
                        values: [`${id}`],
                    }),
                ],
                settings: new ExecuteScriptActionSettings({
                    function: 'run',
                    source: 'function run(identity) {\n return (identity !== undefined) ? encodeURIComponent(identity) : identity;\n}',
                    inputVariables: [
                        'input.message.fromidentity',
                    ],
                    outputVariable: 'helpDeskEncodedFromIdentity',
                }),
            }),
            new ActionViewModel({
                type: ActionType.ProcessCommand,
                $title: 'GetOpenTicket',
                $invalid: false,
                conditions: [
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'state.previous.id',
                        comparison: ConditionComparison.NotEquals,
                        values: [`${id}`],
                    }),
                ],
                settings: new ProcessCommandActionSettings({
                    from: '{{application.identifier}}@{{application.domain}}',
                    to: `postmaster@desk.${this.MESSAGINGHUBDOMAIN}`,
                    method: CommandMethods.get,
                    uri: '/tickets?$filter=customerIdentity%20eq%20%27{{helpDeskEncodedFromIdentity}}%27%20and%20%28status%20eq%20%27Open%27%20or%20status%20eq%20%27Waiting%27%20or%20status%20eq%20%27Assigned%27%29',
                    metadata: {
                        'server.shouldStore': true,
                    },
                    variable: 'helpDeskGetOpenTicketCommandResponse',
                }),
            }),
            new ActionViewModel({
                type: ActionType.ExecuteScript,
                $title: 'TreatGetOpenTicketResponse',
                $invalid: false,
                conditions: [
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'state.previous.id',
                        comparison: ConditionComparison.NotEquals,
                        values: [`${id}`],
                    }),
                ],
                settings: new ExecuteScriptActionSettings({
                    function: 'run',
                    source: 'function run(commandResponse) {\n    var response = JSON.parse(commandResponse)\n    var ticketExists = (response && response.resource && response.resource.items && response.resource.items.length > 0);\n  return ticketExists ? \"true\" : \"false\";\n}',
                    inputVariables: [
                        'helpDeskGetOpenTicketCommandResponse',
                    ],
                    outputVariable: 'helpDeskHasTicket',
                }),
            }),
        ];
        checkAndOpenTicketEnteringCustomActions.push(new ActionViewModel({
            type: ActionType.ProcessCommand,
            $title: 'OpenTicketIfNotFound',
            $invalid: false,
            conditions: [helpDeskHasNoTicketCondition],
            settings: new ProcessCommandActionSettings({
                from: '{{application.identifier}}@{{application.domain}}',
                to: `postmaster@desk.${this.MESSAGINGHUBDOMAIN}`,
                method: CommandMethods.set,
                uri: '/tickets/{{helpDeskEncodedFromIdentity}}',
                variable: 'helpDeskOpenTicketCommandResponse',
                type: 'text/plain',
                resource: '{{input.content}}',
                metadata: {
                    'server.shouldStore': true,
                },
            }),
        }));
        checkAndOpenTicketEnteringCustomActions.push(new ActionViewModel({
            type: ActionType.ExecuteScript,
            $title: 'TreatOpenTicketResponse',
            $invalid: false,
            conditions: [helpDeskHasNoTicketCondition],
            settings: new ExecuteScriptActionSettings({
                function: 'run',
                source: 'function run(commandResponse) {\n    var response = commandResponse ? JSON.parse(commandResponse) : {}\n    var ticketExists = response && response.type === \"application/vnd.iris.ticket+json\" && response.resource \n    return ticketExists ? \"true\" : \"false\";\n}',
                inputVariables: [
                    'helpDeskOpenTicketCommandResponse',
                ],
                outputVariable: 'helpDeskHasTicket',
            }),
        }));
        let enteringCustomActions = [
            ...checkAndOpenTicketEnteringCustomActions,
            new ActionViewModel({
                type: ActionType.ForwardMessageToDesk,
                conditions: [
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'helpDeskHasTicket',
                        comparison: ConditionComparison.Equals,
                        values: ['true'],
                    }),
                ],
                settings: new ForwardMessageToDeskActionSettings(),
            }),
        ];
        if (this.mergeTunnelContactDeskStateActionEnabled) {
            enteringCustomActions = [
                ...this.createDeskStateMergeTunnelContactActions(id),
                ...enteringCustomActions,
            ];
        }
        const conditionOutputs = [
            new OutputViewModel({
                $isDeskOutput: true,
                conditions: [
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'input.type',
                        comparison: ConditionComparison.Equals,
                        values: [DESK_TICKET_TYPE],
                    }),
                    new Condition({
                        source: ValueSource.Context,
                        variable: DESK_TICKET_STATUS_VARIABLE,
                        comparison: ConditionComparison.Equals,
                        values: [TicketStatus.ClosedAttendant],
                    }),
                ],
            }),
            new OutputViewModel({
                $isDeskOutput: true,
                conditions: [
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'input.type',
                        comparison: ConditionComparison.Equals,
                        values: [DESK_TICKET_TYPE],
                    }),
                    new Condition({
                        source: ValueSource.Context,
                        variable: DESK_TICKET_STATUS_VARIABLE,
                        comparison: ConditionComparison.Equals,
                        values: [TicketStatus.ClosedClient],
                    }),
                ],
            }),
            new OutputViewModel({
                $isDeskOutput: true,
                conditions: [
                    new Condition({
                        source: ValueSource.Context,
                        variable: 'input.type',
                        comparison: ConditionComparison.Equals,
                        values: [DESK_TICKET_TYPE],
                    }),
                    new Condition({
                        source: ValueSource.Context,
                        variable: DESK_TICKET_STATUS_VARIABLE,
                        comparison: ConditionComparison.Equals,
                        values: [TicketStatus.ClosedClientInactivity],
                    }),
                ],
            }),
        ];
        const leavingCustomActionsStatus = [
            TicketStatus.ClosedAttendant,
            TicketStatus.ClosedClient,
            TicketStatus.ClosedClientInactivity,
        ];
        const deskVersion = `${this.deskStateVersion.toFixed(1)}.${DESK_STATE_BUILDER_VERSION_NUMBER}`;
        return new StateViewModel({
            id: id,
            deskStateVersion: deskVersion,
            root: false,
            $contentActions: [
                new ContentActionViewModel({
                    input: InputViewModel.create(this.$translate.instant(USER_INPUT_TRANSLATE_KEY), new Input({
                        bypass: false,
                        conditions: [
                            new Condition({
                                source: ValueSource.Context,
                                variable: 'helpDeskHasTicket',
                                comparison: ConditionComparison.Equals,
                                values: ['true'],
                            }),
                        ],
                    })),
                }),
            ],
            $title: title,
            $enteringCustomActions: enteringCustomActions,
            $leavingCustomActions: [
                new ActionViewModel({
                    type: ActionType.SetVariable,
                    $title: 'ResetHasTicket',
                    $invalid: false,
                    conditions: [
                        new Condition({
                            source: ValueSource.Context,
                            variable: 'input.type',
                            comparison: ConditionComparison.Equals,
                            values: [DESK_TICKET_TYPE],
                        }),
                        new Condition({
                            source: ValueSource.Context,
                            variable: DESK_TICKET_STATUS_VARIABLE,
                            comparison: ConditionComparison.Equals,
                            values: leavingCustomActionsStatus,
                        }),
                    ],
                    settings: {
                        variable: 'helpDeskHasTicket',
                        value: 'false',
                        $invalid: false,
                    },
                }),
            ],
            $conditionOutputs: conditionOutputs,
            $defaultOutput: new OutputViewModel({ stateId: id }),
        });
    }
}
BuilderStateService.$inject = ['$translate', 'MESSAGINGHUBDOMAIN'];
